import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Code, Database, Zap, Shield, Users, TrendingUp, CheckCircle, Star } from 'lucide-react'

const Home = () => {
  const services = [
    {
      icon: <Code className="w-8 h-8" data-component-path="client/src/pages/Home.jsx" data-component-line="1:1" data-component-id="client/src/pages/Home.jsx:1:1" />,
      title: 'Web Development',
      description: 'Custom web applications built with cutting-edge technologies',
      link: '/services/web-development'
    },
    {
      icon: <Database className="w-8 h-8" data-component-path="client/src/pages/Home.jsx" data-component-line="2:1" data-component-id="client/src/pages/Home.jsx:2:1" />,
      title: 'SaaS Development',
      description: 'Scalable multi-tenant platforms for modern businesses',
      link: '/services/saas-development'
    },
    {
      icon: <Zap className="w-8 h-8" data-component-path="client/src/pages/Home.jsx" data-component-line="3:1" data-component-id="client/src/pages/Home.jsx:3:1" />,
      title: 'AI & Automation',
      description: 'Intelligent systems that transform business operations',
      link: '/services/ai-automation'
    },
    {
      icon: <Shield className="w-8 h-8" data-component-path="client/src/pages/Home.jsx" data-component-line="4:1" data-component-id="client/src/pages/Home.jsx:4:1" />,
      title: 'ERP Solutions',
      description: 'Enterprise resource planning for operational excellence',
      link: '/services/erp-solutions'
    },
  ]

  const stats = [
    { number: '500+', label: 'Projects Delivered' },
    { number: '200+', label: 'Happy Clients' },
    { number: '50+', label: 'ERP Implementations' },
    { number: '99%', label: 'Client Satisfaction' },
  ]

  const caseStudies = [
    {
      title: 'Global Manufacturing ERP',
      industry: 'Manufacturing',
      result: '40% efficiency increase',
      image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Multi-Tenant SaaS Platform',
      industry: 'FinTech',
      result: '10,000+ active users',
      image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Odoo E-Commerce Integration',
      industry: 'Retail',
      result: '3x revenue growth',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
  ]

  return (
    <div className="min-h-screen" data-component-path="client/src/pages/Home.jsx" data-component-line="5:1" data-component-id="client/src/pages/Home.jsx:5:1">
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-component-path="client/src/pages/Home.jsx"
        data-component-line="6:3"
        data-component-id="client/src/pages/Home.jsx:6:3"
      >
        <div className="absolute inset-0 bg-dark-primary/80" data-component-path="client/src/pages/Home.jsx" data-component-line="14:5" data-component-id="client/src/pages/Home.jsx:14:5"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="16:5" data-component-id="client/src/pages/Home.jsx:16:5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-component-path="client/src/pages/Home.jsx"
            data-component-line="17:7"
            data-component-id="client/src/pages/Home.jsx:17:7"
          >
            <div className="inline-block px-4 py-2 bg-neon-green/10 border border-neon-green rounded-full mb-6" data-component-path="client/src/pages/Home.jsx" data-component-line="22:9" data-component-id="client/src/pages/Home.jsx:22:9">
              <span className="text-neon-green font-semibold text-sm" data-component-path="client/src/pages/Home.jsx" data-component-line="23:11" data-component-id="client/src/pages/Home.jsx:23:11">Official Odoo Partner</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-component-path="client/src/pages/Home.jsx" data-component-line="25:9" data-component-id="client/src/pages/Home.jsx:25:9">
              Enterprise Software.<br data-component-path="client/src/pages/Home.jsx" data-component-line="26:34" data-component-id="client/src/pages/Home.jsx:26:34" />
              <span className="text-gradient" data-component-path="client/src/pages/Home.jsx" data-component-line="27:11" data-component-id="client/src/pages/Home.jsx:27:11">Intelligent Automation.</span><br data-component-path="client/src/pages/Home.jsx" data-component-line="27:62" data-component-id="client/src/pages/Home.jsx:27:62" />
              Odoo Excellence.
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="30:9" data-component-id="client/src/pages/Home.jsx:30:9">
              Transform your business with cutting-edge software solutions, enterprise ERP systems, 
              and intelligent automation powered by our Odoo expertise.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-component-path="client/src/pages/Home.jsx" data-component-line="34:9" data-component-id="client/src/pages/Home.jsx:34:9">
              <Link to="/contact" data-component-path="client/src/pages/Home.jsx" data-component-line="35:11" data-component-id="client/src/pages/Home.jsx:35:11">
                <button className="h-14 px-8 bg-neon-green text-dark-primary font-bold rounded-xl hover:shadow-lg hover:shadow-neon-green/50 transition-all duration-200 flex items-center justify-center gap-2" data-component-path="client/src/pages/Home.jsx" data-component-line="36:13" data-component-id="client/src/pages/Home.jsx:36:13">
                  Start Your Project <ArrowRight className="w-5 h-5" data-component-path="client/src/pages/Home.jsx" data-component-line="37:36" data-component-id="client/src/pages/Home.jsx:37:36" />
                </button>
              </Link>
              <Link to="/odoo-partner" data-component-path="client/src/pages/Home.jsx" data-component-line="40:11" data-component-id="client/src/pages/Home.jsx:40:11">
                <button className="h-14 px-8 glass-panel neon-border text-white font-semibold rounded-xl hover:bg-neon-green/10 transition-all duration-200 flex items-center justify-center gap-2" data-component-path="client/src/pages/Home.jsx" data-component-line="41:13" data-component-id="client/src/pages/Home.jsx:41:13">
                  Explore Odoo Services
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-dark-secondary" data-component-path="client/src/pages/Home.jsx" data-component-line="49:3" data-component-id="client/src/pages/Home.jsx:49:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="50:5" data-component-id="client/src/pages/Home.jsx:50:5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-component-path="client/src/pages/Home.jsx" data-component-line="51:7" data-component-id="client/src/pages/Home.jsx:51:7">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-component-path="client/src/pages/Home.jsx"
                data-component-line="53:11"
                data-component-id="client/src/pages/Home.jsx:53:11"
              >
                <h3 className="text-4xl md:text-5xl font-bold text-neon-green mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="59:13" data-component-id="client/src/pages/Home.jsx:59:13">{stat.number}</h3>
                <p className="text-gray-400" data-component-path="client/src/pages/Home.jsx" data-component-line="60:13" data-component-id="client/src/pages/Home.jsx:60:13">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-primary" data-component-path="client/src/pages/Home.jsx" data-component-line="67:3" data-component-id="client/src/pages/Home.jsx:67:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="68:5" data-component-id="client/src/pages/Home.jsx:68:5">
          <div className="text-center mb-16" data-component-path="client/src/pages/Home.jsx" data-component-line="69:7" data-component-id="client/src/pages/Home.jsx:69:7">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-component-path="client/src/pages/Home.jsx" data-component-line="70:9" data-component-id="client/src/pages/Home.jsx:70:9">Our <span className="text-gradient" data-component-path="client/src/pages/Home.jsx" data-component-line="70:61" data-component-id="client/src/pages/Home.jsx:70:61">Services</span></h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="71:9" data-component-id="client/src/pages/Home.jsx:71:9">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-component-path="client/src/pages/Home.jsx" data-component-line="76:7" data-component-id="client/src/pages/Home.jsx:76:7">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass-panel rounded-xl p-6 hover:neon-border transition-all cursor-pointer group"
                data-component-path="client/src/pages/Home.jsx"
                data-component-line="78:11"
                data-component-id="client/src/pages/Home.jsx:78:11"
              >
                <Link to={service.link} data-component-path="client/src/pages/Home.jsx" data-component-line="86:13" data-component-id="client/src/pages/Home.jsx:86:13">
                  <div className="w-16 h-16 bg-neon-green/10 rounded-xl flex items-center justify-center mb-4 text-neon-green group-hover:bg-neon-green/20 transition-all" data-component-path="client/src/pages/Home.jsx" data-component-line="87:15" data-component-id="client/src/pages/Home.jsx:87:15">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white" data-component-path="client/src/pages/Home.jsx" data-component-line="90:15" data-component-id="client/src/pages/Home.jsx:90:15">{service.title}</h3>
                  <p className="text-gray-400 text-sm mb-4" data-component-path="client/src/pages/Home.jsx" data-component-line="91:15" data-component-id="client/src/pages/Home.jsx:91:15">{service.description}</p>
                  <div className="flex items-center gap-2 text-neon-green text-sm font-semibold" data-component-path="client/src/pages/Home.jsx" data-component-line="92:15" data-component-id="client/src/pages/Home.jsx:92:15">
                    Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-component-path="client/src/pages/Home.jsx" data-component-line="93:28" data-component-id="client/src/pages/Home.jsx:93:28" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12" data-component-path="client/src/pages/Home.jsx" data-component-line="100:7" data-component-id="client/src/pages/Home.jsx:100:7">
            <Link to="/services" data-component-path="client/src/pages/Home.jsx" data-component-line="101:9" data-component-id="client/src/pages/Home.jsx:101:9">
              <button className="h-11 px-6 glass-panel neon-border text-white font-semibold rounded-xl hover:bg-neon-green/10 transition-all" data-component-path="client/src/pages/Home.jsx" data-component-line="102:11" data-component-id="client/src/pages/Home.jsx:102:11">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-secondary" data-component-path="client/src/pages/Home.jsx" data-component-line="110:3" data-component-id="client/src/pages/Home.jsx:110:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="111:5" data-component-id="client/src/pages/Home.jsx:111:5">
          <div className="text-center mb-16" data-component-path="client/src/pages/Home.jsx" data-component-line="112:7" data-component-id="client/src/pages/Home.jsx:112:7">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-component-path="client/src/pages/Home.jsx" data-component-line="113:9" data-component-id="client/src/pages/Home.jsx:113:9">
              Success <span className="text-gradient" data-component-path="client/src/pages/Home.jsx" data-component-line="114:23" data-component-id="client/src/pages/Home.jsx:114:23">Stories</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="116:9" data-component-id="client/src/pages/Home.jsx:116:9">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-component-path="client/src/pages/Home.jsx" data-component-line="121:7" data-component-id="client/src/pages/Home.jsx:121:7">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-panel rounded-xl overflow-hidden hover:neon-border transition-all group"
                data-component-path="client/src/pages/Home.jsx"
                data-component-line="123:11"
                data-component-id="client/src/pages/Home.jsx:123:11"
              >
                <div className="relative h-48 overflow-hidden" data-component-path="client/src/pages/Home.jsx" data-component-line="130:13" data-component-id="client/src/pages/Home.jsx:130:13">
                  <img 
                    src={study.image} 
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800'
                    }}
                    data-component-path="client/src/pages/Home.jsx"
                    data-component-line="131:15"
                    data-component-id="client/src/pages/Home.jsx:131:15"
                  />
                </div>
                <div className="p-6" data-component-path="client/src/pages/Home.jsx" data-component-line="142:13" data-component-id="client/src/pages/Home.jsx:142:13">
                  <div className="text-neon-green text-sm font-semibold mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="143:15" data-component-id="client/src/pages/Home.jsx:143:15">{study.industry}</div>
                  <h3 className="text-xl font-bold mb-3 text-white" data-component-path="client/src/pages/Home.jsx" data-component-line="144:15" data-component-id="client/src/pages/Home.jsx:144:15">{study.title}</h3>
                  <div className="flex items-center gap-2 text-gray-400" data-component-path="client/src/pages/Home.jsx" data-component-line="145:15" data-component-id="client/src/pages/Home.jsx:145:15">
                    <TrendingUp className="w-5 h-5 text-neon-green" data-component-path="client/src/pages/Home.jsx" data-component-line="146:17" data-component-id="client/src/pages/Home.jsx:146:17" />
                    <span data-component-path="client/src/pages/Home.jsx" data-component-line="147:17" data-component-id="client/src/pages/Home.jsx:147:17">{study.result}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12" data-component-path="client/src/pages/Home.jsx" data-component-line="154:7" data-component-id="client/src/pages/Home.jsx:154:7">
            <Link to="/portfolio" data-component-path="client/src/pages/Home.jsx" data-component-line="155:9" data-component-id="client/src/pages/Home.jsx:155:9">
              <button className="h-11 px-6 glass-panel neon-border text-white font-semibold rounded-xl hover:bg-neon-green/10 transition-all" data-component-path="client/src/pages/Home.jsx" data-component-line="156:11" data-component-id="client/src/pages/Home.jsx:156:11">
                View All Case Studies
              </button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-primary" data-component-path="client/src/pages/Home.jsx" data-component-line="164:3" data-component-id="client/src/pages/Home.jsx:164:3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="165:5" data-component-id="client/src/pages/Home.jsx:165:5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="glass-panel rounded-2xl p-12 neon-border"
            data-component-path="client/src/pages/Home.jsx"
            data-component-line="166:7"
            data-component-id="client/src/pages/Home.jsx:166:7"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-component-path="client/src/pages/Home.jsx" data-component-line="171:9" data-component-id="client/src/pages/Home.jsx:171:9">
              Ready to Transform Your <span className="text-gradient" data-component-path="client/src/pages/Home.jsx" data-component-line="172:47" data-component-id="client/src/pages/Home.jsx:172:47">Business</span>?
            </h2>
            <p className="text-gray-400 text-lg mb-8" data-component-path="client/src/pages/Home.jsx" data-component-line="174:9" data-component-id="client/src/pages/Home.jsx:174:9">
              Let's discuss how our enterprise solutions can drive your success
            </p>
            <Link to="/contact" data-component-path="client/src/pages/Home.jsx" data-component-line="177:9" data-component-id="client/src/pages/Home.jsx:177:9">
              <button className="h-14 px-8 bg-neon-green text-dark-primary font-bold rounded-xl hover:shadow-lg hover:shadow-neon-green/50 transition-all duration-200 flex items-center justify-center gap-2 mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="178:11" data-component-id="client/src/pages/Home.jsx:178:11">
                Schedule Consultation <ArrowRight className="w-5 h-5" data-component-path="client/src/pages/Home.jsx" data-component-line="179:42" data-component-id="client/src/pages/Home.jsx:179:42" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home